import * as React from 'react';
import { DayPicker } from 'react-day-picker';

import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button';
import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
        month: 'space-y-4',
        caption: 'flex justify-center pt-1 relative items-center',
        caption_label: 'text-sm font-medium',
        nav: 'space-x-1 flex items-center',
        nav_button: cn(
          buttonVariants({ variant: 'outline' }),
          'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100'
        ),
        nav_button_previous: 'absolute left-1',
        nav_button_next: 'absolute right-1',
        table: 'w-full border-collapse space-y-1',
        head_row: 'flex',
        head_cell:
          'text-muted-foreground rounded-md w-8 font-normal text-[0.8rem] dark:text-muted-foreground',
        row: 'flex w-full mt-2',
        cell: cn(
          'relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-muted [&:has([aria-selected].day-outside)]:bg-muted/50 dark:[&:has([aria-selected])]:bg-muted dark:[&:has([aria-selected].day-outside)]:bg-muted/50',
          props.mode === 'range'
            ? '[&:has(>.day-range-end:not(.day-outside))]:rounded-r-full [&:has(>.day-range-start:not(.day-outside))]:rounded-l-full [&:has(>.day-outside)]:rounded-none'
            : '[&:has([aria-selected])]:rounded-md [&:has([aria-selected].day-outside)]:rounded-none'
        ),
        day: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-8 w-8 flex items-center justify-center font-normal aria-selected:opacity-100 rounded-md',
          '[&.day-outside]:!rounded-none [&.day-outside.day-range-start]:!rounded-none [&.day-outside.day-range-end]:!rounded-none [&.day-outside.day-range-middle]:!rounded-none',
          '[&.day-range-start]:!rounded-full [&.day-range-start]:!bg-primary [&.day-range-start]:!text-primary-foreground [&.day-range-end]:!rounded-full [&.day-range-end]:!bg-primary [&.day-range-end]:!text-primary-foreground',
          'dark:[&.day-range-start]:!bg-primary dark:[&.day-range-start]:!text-primary-foreground dark:[&.day-range-end]:!bg-primary dark:[&.day-range-end]:!text-primary-foreground dark:[&.day-range-start.aria-selected]:!text-primary-foreground dark:[&.day-range-end.aria-selected]:!text-primary-foreground'
        ),
        day_range_start:
          'day-range-start !rounded-full !bg-primary !text-primary-foreground dark:!bg-primary dark:!text-primary-foreground dark:aria-selected:!text-primary-foreground',
        day_range_end:
          'day-range-end !rounded-full !bg-primary !text-primary-foreground dark:!bg-primary dark:!text-primary-foreground dark:aria-selected:!text-primary-foreground',
        day_selected:
          'font-bold text-foreground hover:bg-transparent focus:bg-transparent',
        day_today:
          'bg-muted text-foreground dark:bg-muted dark:text-foreground',
        day_outside:
          'day-outside text-muted-foreground aria-selected:bg-muted/50 aria-selected:text-muted-foreground dark:aria-selected:bg-muted/50 dark:aria-selected:text-muted-foreground rounded-none',
        day_disabled:
          'text-muted-foreground opacity-50 dark:text-muted-foreground',
        day_range_middle:
          'aria-selected:bg-muted aria-selected:text-foreground dark:aria-selected:bg-muted dark:aria-selected:text-foreground',
        day_hidden: 'invisible',
        ...classNames,
      }}
      components={{
        IconLeft: ({ ...props }) => (
          <ChevronLeftIcon className={cn('h-4 w-4', props.className)} />
        ),
        IconRight: ({ ...props }) => (
          <ChevronRightIcon className={cn('h-4 w-4', props.className)} />
        ),
      }}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
